import { faCaretDown, faCheckCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { FACET_NAMES } from 'mk/bazaar/common/enums';
import FacetDropdown, { Option } from 'mk2/apps/bazaar/components/FacetDropdown';
import { OptionKey } from 'mk2/apps/bazaar/components/FacetDropdownOption';
import { Modal } from 'mk2/components/Modal';
import useEventCallback from 'mk2/hooks/useEventCallback';
import React, { useMemo } from 'react';
import styles from './Facet.mscss';

export interface OwnProps {
    className?: string;
    isMobile: boolean;
    opened?: boolean;
    dimmed?: boolean;
    facetKey: string;
    options: Option[];
    search?: boolean;
    selected?: OptionKey[];
    alwaysDisplayed?: OptionKey[];
    label?: React.ReactNode;
    multiselect?: boolean;
    isFilter?: boolean;
    canClear?: boolean;

    // This options will be always displayed first, then selected, then rest
    settingsOptions?: OptionKey[];

    onToggle?(facetKey: string);
    onClear?(facetKey: string);
    onChange(facetKey: string, optionKey: OptionKey, checked: boolean);
}

const emptyArray = [];

type Props = OwnProps;

const Facet: React.FunctionComponent<Props> = ({
    className,
    isMobile,
    opened = false,
    dimmed = false,
    facetKey,
    options,
    search,
    selected = emptyArray,
    multiselect = false,
    alwaysDisplayed = emptyArray,
    isFilter = true,
    canClear = true,
    label,
    onChange,
    onToggle,
    onClear,
    settingsOptions,
}) => {
    const msgId = `BZ2_FACET_${facetKey.toUpperCase()}`;
    label = label || FACET_NAMES[msgId] || msgId;

    // Ignore settings options e.g. in locations select there is switch to display 25km radius
    const selectedWithoutSettings = useMemo((): OptionKey[] => selected.filter((s) => !settingsOptions?.includes(s)), [
        selected,
        settingsOptions,
    ]);

    const selectedLabel = useMemo(
        () =>
            options
                .filter((o) => selectedWithoutSettings.includes(o.key))
                .map((opt) => {
                    return (
                        <React.Fragment key={opt.key}>
                            <span
                                className={cx(
                                    styles.Facet__selected__icon,
                                    styles[`Facet__selected__icon--${facetKey}`],
                                    styles[`Facet__selected__icon--${facetKey}-${opt.key}`],
                                )}
                            />
                            <span
                                className={cx(
                                    styles.Facet__selected__label,
                                    styles[`Facet__selected__label--${facetKey}`],
                                    styles[`Facet__selected__label--${facetKey}-${opt.key}`],
                                )}
                            >
                                {opt.label}
                            </span>
                            <span
                                className={cx(
                                    styles.Facet__selected__delimiter,
                                    styles[`Facet__selected__delimiter--${facetKey}`],
                                    styles[`Facet__selected__delimiter--${facetKey}-${opt.key}`],
                                )}
                            >
                                ,{' '}
                            </span>
                        </React.Fragment>
                    );
                }),
        [options, selectedWithoutSettings],
    );

    const onToggleCallback = useEventCallback(() => {
        if (onToggle) {
            onToggle(facetKey);
        }
    }, [facetKey, onToggle]);
    const onClearCallback = useEventCallback(() => {
        if (onClear) {
            onClear(facetKey);
        }
    }, [facetKey, onClear]);

    return (
        <div className={cx(styles.Facet, className, opened ? styles['Facet--opened'] : styles['Facet--closed'])}>
            <div className={cx(styles.Facet__shadow, opened && styles['Facet__shadow--active'])} />
            {selectedWithoutSettings.length > 0 && !opened && canClear && (
                <div className={styles.Facet__clear} onClick={onClearCallback}>
                    <div className={cx(styles.Facet__clear__icon, !isFilter && styles['Facet__clear__icon--select'])}>
                        <FontAwesomeIcon icon={faTimesCircle} />
                    </div>
                </div>
            )}
            <div
                className={cx(
                    styles.Facet__wrapper,
                    opened && styles['Facet__wrapper--opened'],
                    selectedWithoutSettings.length > 0 && !opened && styles['Facet__wrapper--hasValue'],
                    selectedWithoutSettings.length > 0 &&
                        !opened &&
                        !isFilter &&
                        styles['Facet__wrapper--hasValue--select'],
                    selectedWithoutSettings.length > 1 && styles['Facet__wrapper--hasMoreValues'],
                )}
                data-cy={`bazaar-facet-${facetKey}`}
                onClick={onToggleCallback}
            >
                {dimmed && <div className={styles.Facet__dimmed} />}
                <div
                    className={cx(
                        styles.Facet__label,
                        selectedWithoutSettings.length && styles['Facet__label--selected'],
                    )}
                >
                    <span
                        className={cx(styles.Facet__label__container, styles[`Facet__label__container--${facetKey}`])}
                    >
                        {selectedWithoutSettings.length ? selectedLabel : label}
                    </span>
                    <div className={styles.Facet__mask} />
                    <div
                        className={cx(
                            styles.Facet__label__icon,
                            isFilter || selectedWithoutSettings.length === 0
                                ? styles['Facet__label__icon--filter']
                                : styles['Facet__label__icon--select'],
                        )}
                    >
                        <FontAwesomeIcon
                            icon={isFilter || selectedWithoutSettings.length === 0 ? faCaretDown : faCheckCircle}
                        />
                    </div>
                </div>
            </div>
            {selectedWithoutSettings.length > 1 && (
                <div className={cx(styles.Facet__moreSelected, !isFilter && styles['Facet__moreSelected--select'])} />
            )}

            {isMobile ? (// this should fix iphone safari bug with invisible header
                <Modal className={styles.Facet__dropdown} show={opened} backdrop={false}>
                    <FacetDropdown
                        alwaysDisplayed={alwaysDisplayed}
                        multiselect={multiselect}
                        facetKey={facetKey}
                        options={options}
                        onChange={onChange}
                        onClose={onToggleCallback}
                        search={search}
                        selected={selected}
                        settingsOptions={settingsOptions}
                    />
                </Modal>
            ) : opened ? (
                <div className={styles.Facet__dropdown}>
                    <FacetDropdown
                        alwaysDisplayed={alwaysDisplayed}
                        multiselect={multiselect}
                        facetKey={facetKey}
                        options={options}
                        onChange={onChange}
                        onClose={onToggleCallback}
                        search={search}
                        selected={selected}
                        settingsOptions={settingsOptions}
                    />
                </div>
            ) : null}
        </div>
    );
};

Facet.displayName = 'Facet';

export default React.memo<Props>(Facet);
